import moment from "moment-timezone";
import React from "react";

import { HackerJudgingStatus } from "src/api/types.generated";
import { Link } from "src/shared/components";
import { Route } from "src/shared/constants/route";
import {
  CheckedDocumentIcon,
  CheckCircleInvertedGreen,
  DocumentIcon,
  ErrorExclamationIcon,
  FlagIcon,
  InfoIcon,
  JudgingIcon,
  UsersIcon,
  WarningCircleIcon,
} from "src/static/icons";

export type ScheduleStep = {
  id: string;
  icon: React.ReactNode;
  title: string;
  time: React.ReactNode;
  description: string;
};

// Judging round start times are stored in meta as UTC and always displayed in EDT.
const JUDGING_TIMEZONE = "America/Toronto";
const JUDGING_TIME_FORMAT = "ddd · h:mm A";

const formatJudgingTime = (isoTime?: string | null): React.ReactNode => {
  if (isoTime) {
    const time = moment.tz(isoTime, JUDGING_TIMEZONE);
    if (time.isValid()) return time.format(JUDGING_TIME_FORMAT);
  }

  return <Link href={Route.HACKER_JUDGING_FAQ}>Check time here</Link>;
};

export const getJudgingScheduleSteps = (
  r1StartTime?: string | null,
  r2StartTime?: string | null,
  draftDeadline?: string | null,
  finalDeadline?: string | null
): ScheduleStep[] => [
  {
    id: "devpost-draft-due",
    icon: <DocumentIcon />,
    title: "Devpost Draft Due",
    time: formatJudgingTime(draftDeadline),
    description:
      "Create your project and add every teammate's badge ID. You may edit until 8AM on Sunday.",
  },
  {
    id: "final-devpost-project-due",
    icon: <CheckedDocumentIcon />,
    title: "Final Devpost Project Due",
    time: formatJudgingTime(finalDeadline),
    description:
      "Last chance to submit your project on Devpost. After this, no more edits are allowed.",
  },
  {
    id: "round-1-judging",
    icon: <JudgingIcon />,
    title: "Round 1 Judging",
    time: formatJudgingTime(r1StartTime),
    description:
      "Demo your project to a pair of judges during your assigned judging slot!",
  },
  {
    id: "round-2-judging",
    icon: <UsersIcon />,
    title: "Round 2 Judging",
    time: formatJudgingTime(r2StartTime),
    description:
      "Selected teams demo projects at assigned tables to judges in a science-fair format.",
  },
  {
    id: "closing-ceremonies",
    icon: <FlagIcon />,
    title: "Closing Ceremonies",
    time: "Sun · 2:30 PM",
    description:
      "Finalists demo projects on stage at Lazaridis Hall! Finalist and Sponsor prizes awarded.",
  },
];

export type DevpostCountdown = {
  deadline: Date;
  mode: "draft" | "final";
};

const pad2 = (value: number) => value.toString().padStart(2, "0");

const parseDeadline = (isoTime?: string | null): Date | null => {
  if (!isoTime) return null;
  const d = new Date(isoTime);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isNoProjectStatus = (status: HackerJudgingStatus | undefined) =>
  !status || status === HackerJudgingStatus.NoSubmission;

export const getDevpostCountdown = (
  now: Date,
  status: HackerJudgingStatus | undefined,
  draftDeadlineIso?: string | null,
  finalDeadlineIso?: string | null
): DevpostCountdown | null => {
  const draftDeadline = parseDeadline(draftDeadlineIso);

  if (isNoProjectStatus(status) && draftDeadline && now < draftDeadline) {
    return { deadline: draftDeadline, mode: "draft" };
  }

  const finalDeadline = parseDeadline(finalDeadlineIso);
  if (finalDeadline && now < finalDeadline) {
    return { deadline: finalDeadline, mode: "final" };
  }

  return null;
};

export const formatDevpostCountdown = (deadline: Date, now: Date): string => {
  const remainingMs = Math.max(0, deadline.getTime() - now.getTime());
  const totalSeconds = Math.floor(remainingMs / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}h ${pad2(minutes)}m ${pad2(seconds)}s`;
};

/** Returns only the most significant time unit, e.g. "24h", "45m", "30s". Used in the mobile banner. */
export const formatMostSignificantUnit = (
  deadline: Date,
  now: Date
): string => {
  const remainingMs = Math.max(0, deadline.getTime() - now.getTime());
  const totalSeconds = Math.floor(remainingMs / 1000);

  const hours = Math.floor(totalSeconds / 3600);
  if (hours > 0) return `${hours}h`;

  const minutes = Math.floor(totalSeconds / 60);
  if (minutes > 0) return `${minutes}m`;

  return `${totalSeconds}s`;
};

export enum JudgingBannerVariant {
  Info = "info",
  Success = "success",
  Danger = "danger",
  Warning = "warning",
}

export const JUDGING_BANNER_ICON: Record<JudgingBannerVariant, React.FC> = {
  [JudgingBannerVariant.Info]: InfoIcon,
  [JudgingBannerVariant.Success]: CheckCircleInvertedGreen,
  [JudgingBannerVariant.Danger]: ErrorExclamationIcon,
  [JudgingBannerVariant.Warning]: WarningCircleIcon,
};

export type JudgingStatusBanner = {
  variant: JudgingBannerVariant;
  title: string;
  description: React.ReactNode;
  showDevpostCta?: boolean;
};

export const JUDGING_DEVPOST_URL = "https://hackthenorth2026.devpost.com/";

export const DEVPOST_SYNC_WARNING_BANNER: JudgingStatusBanner = {
  variant: JudgingBannerVariant.Warning,
  title: "Waiting for your Devpost project to appear?",
  description: (
    <>
      <p>
        It can take <u>10 to 15 minutes</u> for our system to sync your project,
        so please be patient.
      </p>
      <p>
        <em>
          Please don&apos;t line up at the Help Desk unless you have waited the
          10 minutes, refreshed this page, and checked again.
        </em>
      </p>
    </>
  ),
};

const formatDeadlineShort = (isoTime?: string | null): string => {
  if (!isoTime) return "the deadline";
  const t = moment.tz(isoTime, JUDGING_TIMEZONE);
  return t.isValid() ? t.format("dddd h:mm A") : "the deadline";
};

const makeNoProjectBeforeBanner = (
  draftDeadlineIso?: string | null,
  finalDeadlineIso?: string | null
): JudgingStatusBanner => ({
  variant: JudgingBannerVariant.Info,
  title: "We can't find your project on Devpost!",
  showDevpostCta: true,
  description: (
    <>
      <p>
        To be eligible for judging, create a project on Devpost and include
        every team member&apos;s badge ID. You must create a submission for your
        project by {formatDeadlineShort(draftDeadlineIso)}, but it&apos;s not
        final! You can continue updating your project until the final submission
        deadline at {formatDeadlineShort(finalDeadlineIso)}.
      </p>
      <p>
        The sooner you submit, the better! There&apos;s no reason not to create
        your submission now!
      </p>
      <p>
        We are only able to match your project if it includes your badge ID (
        <strong>XXXX-XXXX-XXXX-XXXX</strong>). If you&apos;ve created a project
        (even if it hasn&apos;t been submitted yet) but are still seeing this
        message, double-check that your badge ID is entered correctly.
      </p>
    </>
  ),
});

const makeNoProjectAfterBanner = (
  draftDeadlineIso?: string | null
): JudgingStatusBanner => ({
  variant: JudgingBannerVariant.Danger,
  title: "Project Not Submitted",
  description: (
    <p>
      We did not receive a final Devpost submission from your team before the{" "}
      {formatDeadlineShort(draftDeadlineIso)} deadline. Please visit the Help
      Desk if you believe this is a mistake.
    </p>
  ),
});

const makeSubmissionReceivedBanner = (
  finalDeadlineIso?: string | null
): JudgingStatusBanner => ({
  variant: JudgingBannerVariant.Success,
  title: "We've got your project!",
  description: (
    <>
      <p>
        Your Devpost project has been received. You can keep updating your
        project until {formatDeadlineShort(finalDeadlineIso)}!
      </p>
      <p>
        No further action is required right now. Your personalized judging
        information will appear on this page once it is available.
      </p>
    </>
  ),
});

const NOT_ADVANCING_BANNER: JudgingStatusBanner = {
  variant: JudgingBannerVariant.Info,
  title: "Thank You for Participating in Judging",
  description: (
    <p>
      Congrats on demoing your project at Hack the North 2026! Your
      project&apos;s judging is now fully complete. Enjoy the rest of the event
      and see you at Hacky Hour, happening now in E5 Design Bay.
    </p>
  ),
};

export const NO_EVENT_ATTENDEE_BANNER: JudgingStatusBanner = {
  variant: JudgingBannerVariant.Danger,
  title: "We Can't Find Your Hacker Profile",
  description: (
    <p>
      We couldn&apos;t find a hacker profile associated with your account, so
      we&apos;re unable to show your judging status or badge ID. Please visit
      the Help Desk for assistance.
    </p>
  ),
};

export const JUDGING_STATUS_BANNERS: Partial<
  Record<HackerJudgingStatus, JudgingStatusBanner>
> = {
  [HackerJudgingStatus.NoBadgeCode]: {
    variant: JudgingBannerVariant.Danger,
    title: "We Can't Find Your Badge",
    description: (
      <p>
        Something went wrong looking up your badge ID, so we can&apos;t match
        you to a project yet. Please visit the Help Desk with your badge.
      </p>
    ),
  },
  [HackerJudgingStatus.Round_1Scheduled]: {
    variant: JudgingBannerVariant.Info,
    title: "Scheduled for Round 1 Judging",
    description: (
      <p>
        Your team has been scheduled for Round 1 judging. Please arrive at your
        waiting room at least 10 minutes before your judging time to check in.
      </p>
    ),
  },
  [HackerJudgingStatus.Round_1WaitingRoom]: {
    variant: JudgingBannerVariant.Success,
    title: "Checked In for Round 1 Judging",
    description: (
      <p>
        Your team has checked in. Please remain in the waiting room and you will
        be directed to your judging room when it is time.
      </p>
    ),
  },
  [HackerJudgingStatus.Round_1InJudging]: {
    variant: JudgingBannerVariant.Info,
    title: "Round 1 Judging in Progress",
    description: (
      <p>
        Round 1 judging is currently underway. Once your team has completed your
        judging session, stay tuned to this page for Round 2 updates. Results
        will be posted after all Round 1 judging has concluded.
      </p>
    ),
  },
  [HackerJudgingStatus.Round_1NotAdvancing]: NOT_ADVANCING_BANNER,
  [HackerJudgingStatus.Round_2Scheduled]: {
    variant: JudgingBannerVariant.Success,
    title: "You're Advancing to Round 2",
    description: (
      <p>
        Congratulations! Your team has been selected for Round 2 judging. Please
        head to your assigned table on Floor 2 now and check in before judging
        begins.
      </p>
    ),
  },
  [HackerJudgingStatus.Round_2CheckedIn]: {
    variant: JudgingBannerVariant.Success,
    title: "Checked In for Round 2 Judging",
    description: (
      <p>
        Your team is checked in for Round 2. Please remain at your assigned
        table. Three pairs of judges will visit your project.
      </p>
    ),
  },
  [HackerJudgingStatus.Round_2InJudging]: {
    variant: JudgingBannerVariant.Info,
    title: "Round 2 Judging in Progress",
    description: (
      <p>
        Round 2 judging is underway. Once your team has presented to all three
        judging pairs, remain at PSE and keep an eye out for a phone call and
        this dashboard for finalist results.
      </p>
    ),
  },
  [HackerJudgingStatus.Round_2NotAdvancing]: NOT_ADVANCING_BANNER,
  [HackerJudgingStatus.FinalistNotified]: {
    variant: JudgingBannerVariant.Success,
    title: "Congratulations, You're a Finalist!",
    description: (
      <p>
        Your team is a Hack the North 2026 finalist. Please begin heading to
        Lazaridis Hall for closing ceremonies and check in at the designated
        area near the back of the hall when you arrive.
      </p>
    ),
  },
};

export const getJudgingStatusBanner = (
  status: HackerJudgingStatus | undefined,
  now: Date = new Date(),
  draftDeadlineIso?: string | null,
  finalDeadlineIso?: string | null
): JudgingStatusBanner => {
  if (!status || status === HackerJudgingStatus.NoSubmission) {
    const finalDeadline = parseDeadline(finalDeadlineIso);
    return finalDeadline && now >= finalDeadline
      ? makeNoProjectAfterBanner(draftDeadlineIso)
      : makeNoProjectBeforeBanner(draftDeadlineIso, finalDeadlineIso);
  }

  if (status === HackerJudgingStatus.SubmissionReceived) {
    return makeSubmissionReceivedBanner(finalDeadlineIso);
  }

  return (
    JUDGING_STATUS_BANNERS[status] ??
    makeNoProjectBeforeBanner(draftDeadlineIso, finalDeadlineIso)
  );
};

export type JudgingTopBannerContent = {
  variant: JudgingBannerVariant;
  title: string;
  countdown: DevpostCountdown | null;
};

export const getJudgingTopBannerContent = (
  status: HackerJudgingStatus | undefined,
  now: Date,
  draftDeadlineIso?: string | null,
  finalDeadlineIso?: string | null,
  hasNoEventAttendee?: boolean
): JudgingTopBannerContent => {
  const banner = hasNoEventAttendee
    ? NO_EVENT_ATTENDEE_BANNER
    : getJudgingStatusBanner(status, now, draftDeadlineIso, finalDeadlineIso);

  return {
    variant: banner.variant,
    title: banner.title,
    countdown: hasNoEventAttendee
      ? null
      : getDevpostCountdown(now, status, draftDeadlineIso, finalDeadlineIso),
  };
};
